import React, { Fragment } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import makeStyles from "@mui/styles/makeStyles";
import { Link as RouterLink } from "react-router-dom";
import { advertFormatCurrency } from "../../../../../utils";
import { useTranslation } from "react-i18next";
import { generatePath } from "react-router";
import { Skeleton } from "@mui/material";
import useMobileRoutes from "../../../hooks/useMobileRoutes";
import Button from "@/react/components/Common/Button";
const useStyles = makeStyles((theme) => ({
    container: {
        position: "relative",
        backgroundColor: "white",
        whiteSpace: "normal",
        boxShadow: "0px 0px 5px 0px rgb(0 0 0 / 27%)",
        marginBottom: "5px",
        marginTop: "3px",
    },
    imageContainer: {
        display: "block",
        width: "100%",
        paddingTop: "calc(552 / 1184 * 100%)",
        position: "relative",
        "& img, & span": {
            position: "absolute",
            left: 0,
            top: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
        },
    },
    header: {
        color: theme.palette.primary.dark,
        textAlign: "center",
        fontSize: "20px",
        fontWeight: 400,
        lineHeight: 1.167,
        letterSpacing: "0em",
        margin: 0,
    },
    descriptionContainer: {
        fontSize: "12px",
        textAlign: "center",
    },
    priceContainer: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        flexGrow: 1,
        marginRight: theme.spacing(2),
    },
    originalPackagePrice: {
        color: "#9D9D9D",
        textDecoration: "line-through",
        fontSize: "12px",
        textAlign: "center",
    },
    justNowPackagePrice: {
        color: theme.palette.secondary.main,
        fontSize: "15px",
        textAlign: "center",
        fontWeight: 500,
    },
    packagePrice: {
        textAlign: "center",
    },
    packageButton: {
        minWidth: "50%",
        minHeight: "40px",
    },
    disabledContainer: {
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: theme.palette.primary.light + "aa",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    },
    disabledText: {
        padding: "8px 20px",
        fontSize: "18px",
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
    },
}));
function ImageWithContainer({ classes, pkg, customBannerUrl, onClick, }) {
    const routes = useMobileRoutes();
    const bannerSrc = customBannerUrl !== null && customBannerUrl !== void 0 ? customBannerUrl : pkg.image;
    const img = (React.createElement("img", { src: bannerSrc, "aria-labelledby": "package_list_" + pkg.uuid }));
    if ((pkg.type == "link" && pkg.link) || pkg.type === "package") {
        const linkProps = getPackageLink(pkg, routes);
        if ("to" in linkProps) {
            const Cmp = linkProps.component;
            return (React.createElement(Cmp, { className: classes.imageContainer, onClick: onClick, to: linkProps.to }, img));
        }
        else {
            return (React.createElement("a", { className: classes.imageContainer, href: linkProps.href, onClick: onClick }, img));
        }
    }
    return (React.createElement("div", { className: classes.imageContainer },
        React.createElement("img", { src: bannerSrc, "aria-labelledby": "package_list_" + pkg.uuid })));
}
const PackageListItem = ({ pkg, customBannerUrl, onClick, }) => {
    const classes = useStyles();
    const routes = useMobileRoutes();
    return (React.createElement("div", { className: classes.container },
        React.createElement(ImageWithContainer, { classes: classes, pkg: pkg, customBannerUrl: customBannerUrl, onClick: onClick }),
        React.createElement(Box, { p: 2, display: "flex", flexDirection: "column", flexGrow: 1 },
            React.createElement("h3", { className: classes.header, id: "package_list_" + pkg.uuid }, pkg.name),
            React.createElement(Box, { mb: 2, mt: 1, className: classes.descriptionContainer }, pkg.description.split(/\n/).map((line) => (React.createElement(Fragment, { key: line },
                line,
                React.createElement("br", null))))),
            pkg.type == "package" && (React.createElement(Box, { display: "flex", justifyContent: "space-between" },
                React.createElement(PackagePrice, { pkg: pkg }),
                React.createElement(Button, { component: RouterLink, className: classes.packageButton, onClick: onClick, to: generatePath(routes["products.packages.create"].path, {
                        packageUuid: pkg.uuid,
                    }) }, pkg.linkText))),
            pkg.type == "link" && pkg.link && (React.createElement(Box, { display: "flex", justifyContent: "flex-end" },
                React.createElement(Button, Object.assign({ className: classes.packageButton, onClick: onClick }, getPackageLink(pkg, routes)), pkg.linkText)))),
        pkg.disabled && (React.createElement("div", { className: classes.disabledContainer },
            React.createElement("div", { className: classes.disabledText }, pkg.disabled.reason)))));
};
const PackagePrice = ({ pkg }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    if (pkg.useTimeLimitedDiscount &&
        pkg.originalPrice > pkg.timeLimitedDiscountPrice) {
        return (React.createElement("div", { className: classes.priceContainer },
            React.createElement("div", { className: classes.originalPackagePrice }, t("Price: %s", {
                postProcess: "sprintf",
                sprintf: [advertFormatCurrency(pkg.originalPrice)],
            })),
            React.createElement("div", { className: classes.justNowPackagePrice }, t("Just now: %s", {
                postProcess: "sprintf",
                sprintf: [advertFormatCurrency(pkg.timeLimitedDiscountPrice)],
            }))));
    }
    if (!pkg.useTimeLimitedDiscount &&
        pkg.originalPrice > pkg.priceIncludingDiscount) {
        return (React.createElement("div", { className: classes.priceContainer },
            React.createElement("div", { className: classes.originalPackagePrice }, t("Price: %s", {
                postProcess: "sprintf",
                sprintf: [advertFormatCurrency(pkg.originalPrice)],
            })),
            React.createElement("div", { className: classes.justNowPackagePrice }, t("Package price: %s", {
                postProcess: "sprintf",
                sprintf: [advertFormatCurrency(pkg.priceIncludingDiscount)],
            }))));
    }
    if (pkg.priceIncludingDiscount > 0) {
        return (React.createElement("p", { className: classes.packagePrice }, t("Price: %s", {
            postProcess: "sprintf",
            sprintf: [advertFormatCurrency(pkg.priceIncludingDiscount)],
        })));
    }
    return null;
};
const getPackageLink = (pkg, routes) => {
    if (pkg.linkInfo) {
        switch (pkg.linkInfo.type) {
            case "custom-page":
                return {
                    to: generatePath(routes["legacypage"].path, {
                        id: pkg.linkInfo.pageId,
                    }),
                    component: RouterLink,
                };
            case "cards":
                return {
                    to: generatePath(routes["products.cards"].path, {
                        type: pkg.linkInfo.type,
                    }),
                    component: RouterLink,
                };
            case "calendars":
                return {
                    to: generatePath(routes["products.calendars"].path, {
                        type: pkg.linkInfo.type,
                    }),
                    component: RouterLink,
                };
            case "catalogues":
                return {
                    to: generatePath(routes["products.catalogues"].path),
                    component: RouterLink,
                };
            case "digitalcopy":
                return {
                    to: generatePath(routes["products.digitalportraits"].path),
                    component: RouterLink,
                };
            case "photogifts.category":
                console.log("Generate", routes["products.photogifts.category"].path, pkg.linkInfo.category);
                return {
                    to: generatePath(routes["products.photogifts.category"].path, {
                        type: "photogifts",
                        category: pkg.linkInfo.category,
                    }),
                    component: RouterLink,
                };
            case "photogifts":
                return {
                    to: generatePath(routes["products.list"].path),
                    component: RouterLink,
                };
        }
    }
    return {
        href: pkg.link,
        component: "a",
    };
};
export const PackageListItemSkeleton = () => {
    const classes = useStyles();
    return (React.createElement("div", { className: classes.container },
        React.createElement("div", { className: classes.imageContainer },
            React.createElement(Skeleton, { variant: "rectangular" })),
        React.createElement(Box, { p: 2, display: "flex", flexDirection: "column", flexGrow: 1 },
            React.createElement(Typography, { variant: "h6", className: classes.header },
                React.createElement(Skeleton, { variant: "text" })),
            React.createElement("p", { className: "package-price" },
                React.createElement(Skeleton, { variant: "text" })),
            React.createElement(Box, { mb: 1, mt: 1, className: classes.descriptionContainer },
                React.createElement(Skeleton, { variant: "text" }),
                React.createElement(Skeleton, { variant: "text" })),
            React.createElement(Box, { display: "flex", justifyContent: "space-between" },
                React.createElement("div", { style: { flexBasis: "30%" } },
                    React.createElement(Skeleton, { variant: "text", animation: "pulse" })),
                React.createElement(Skeleton, { variant: "rectangular", width: "140", height: 40 })))));
};
export default PackageListItem;
